import { z } from 'zod';
import { ColourToken, NumberFormat, Spacing } from '../presentation-values.mjs';
import { colourKeysFor } from '../../design/presentation-tokens.mjs';

export const COLOUR_CHANNELS = Object.freeze(['text', 'background', 'border', 'fill', 'stroke']);
export const BOX_SPACING = Object.freeze([
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
]);
export const PADDING_SPACING = Object.freeze(
  BOX_SPACING.filter((side) => side.startsWith('padding')),
);
export const GAP_SPACING = Object.freeze(['rowGap', 'columnGap']);

/** Per-node declared destinations, with no defaults inserted into retained bytes. */
export function nativePresentationFields({ colours = [], spacing = [], numeric = false } = {}) {
  if (
    colours.some((key) => !COLOUR_CHANNELS.includes(key)) ||
    spacing.some((key) => ![...BOX_SPACING, ...GAP_SPACING].includes(key))
  )
    throw new TypeError('invalid_presentation_registration');
  const fields = { schemaVersion: z.literal(1) };
  if (colours.length)
    fields.colours = z
      .object(
        Object.fromEntries(
          colours.map((key) => [
            key,
            ColourToken.refine(
              (token) => colourKeysFor(key).includes(token),
              'unadmitted_colour_token',
            ).optional(),
          ]),
        ),
      )
      .strict()
      .optional();
  if (spacing.length)
    fields.spacing = z
      .object(Object.fromEntries(spacing.map((key) => [key, Spacing.optional()])))
      .strict()
      .optional();
  if (numeric) fields.numberFormat = NumberFormat.optional();
  return { presentation: z.object(fields).strict().optional() };
}
export const nativePresentationSchema = nativePresentationFields({
  colours: COLOUR_CHANNELS,
  spacing: [...BOX_SPACING, ...GAP_SPACING],
  numeric: true,
}).presentation.unwrap();

export function numericMetadataForScalar(scalar) {
  if (scalar?.kind !== 'decimal') return null;
  return {
    valueType:
      scalar.currency !== null
        ? 'money'
        : scalar.unit === 'ratio'
          ? 'ratio'
          : scalar.unit === 'count'
            ? 'count'
            : 'number',
    currency: scalar.currency,
    unit: scalar.unit,
    scale: scalar.scale,
  };
}
export function numericMetadataForSlot(slot) {
  return numericMetadataForScalar(slot?.kind === 'literal' ? slot.value : slot?.observed);
}
export function assertDeclaredNumberFormat(value, metadata) {
  const format = NumberFormat.parse(value);
  if (!metadata) throw new TypeError('number_format_metadata_unavailable');
  if (format.style === 'currency' && format.currency !== metadata.currency)
    throw new TypeError('number_format_currency_mismatch');
  if (format.style === 'unit' && format.unit !== metadata.unit)
    throw new TypeError('number_format_unit_mismatch');
  if (format.style === 'percent' && metadata.valueType !== 'ratio')
    throw new TypeError('number_format_ratio_required');
  return format;
}
export function checkNumericPresentation(node, metadata, ctx) {
  if (node.presentation?.numberFormat === undefined) return;
  try {
    assertDeclaredNumberFormat(node.presentation.numberFormat, metadata);
  } catch (error) {
    ctx.addIssue({
      code: 'custom',
      path: ['presentation', 'numberFormat'],
      message: error.message,
    });
  }
}

export function numericMetadataForCell(cell) {
  return cell.kind === 'value'
    ? numericMetadataForSlot(cell.slot)
    : cell.kind === 'formula' && cell.cache.status === 'valid'
      ? numericMetadataForScalar(cell.cache.value)
      : null;
}

export function checkCellPresentation(cell, ctx) {
  if (cell.presentation?.numberFormat !== undefined && cell.numberFormat !== null)
    ctx.addIssue({
      code: 'custom',
      path: ['presentation', 'numberFormat'],
      message: 'competing_number_formats',
    });
  checkNumericPresentation(cell, numericMetadataForCell(cell), ctx);
}
